import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { ServiceBase } from "./service.base";
import { PedidosApi } from "../api/pedidos.api";
import { IPedido, PedidoStatus } from "../models/pedido";
import { IPedidoDTO } from "../dto/pedido-dto";
import { IPrazoDTO } from "../dto/prazo-dto";
import { map } from "rxjs/operators";
import { Tso } from "ts-odata";
import { InfoLogisticaDTO } from "../dto/inf-logistica-dto";
import { InfoBloqueioDeCreditoDTO } from "../dto/info-bloqueio-credito-dto";
import { InfoSeparacaoPedidoDTO } from "../dto/info-separacao-pedido-dto";
import { PedidosSearchFilterViewModel } from "../../../view-models/pedidos-search-filter-view-model";

@Injectable()
export class PedidosService extends ServiceBase<IPedido, IPedidoDTO> {

  constructor(private api: PedidosApi) {
    super(api);
  }

  getQueryList(): Tso {
    return this.api.createListQuery();
  }

  getQueryCsv(): Tso {
    return this.api.createCsvQuery();
  }

  getCsv(query: Tso) {
    return this.api.getCsv(query).pipe(
      map((res) => {
        this.saveFile(res.body, "lista-pedidos.csv", false);
        return res;
      })
    );
  }

  getPedido(id: string): Observable<IPedidoDTO> {
    return this.api.doGet(`${id}`);
  }

  prazo(uf: string): Observable<IPrazoDTO> {
    return this.api.doGet(`prazo/${uf}`);
  }

  reenviar(id: string): Observable<IPedidoDTO> {
    return this.api.doGet(`reenviar/${id}`);
  }

  reenviarEmailsAprovacao(id: string): Observable<any> {
    return this.api.doPost(`reenviar-emails-aprovacao/${id}`, {});
  }

  romaneio(id: string): Observable<Blob> {
    return this.api.doGet(`romaneio.pdf/${id}`, { responseType: "blob" }).pipe(
      map((res) => {
        this.saveFile(res, "romaneio.pdf");
        return res;
      })
    );
  }

  getDadosDeLogisticaDoPedido(id: string): Observable<InfoLogisticaDTO> {
    return this.api.doGet(`info-logistica/${id}`).pipe(map((res) => {
      return new InfoLogisticaDTO(res)
    }));
  }

  getDadosDeSeparacaoDoPedido(id: string): Observable<InfoSeparacaoPedidoDTO> {
    return this.api.doGet(`info-separacao/${id}`).pipe(map((res) => {
      return new InfoSeparacaoPedidoDTO(res)
    }));
  }

  getInfoBloqueiCreditoPedido(id: string): Observable<InfoBloqueioDeCreditoDTO> {
    return this.api.doGet(`info-bloquei-de-credito/${id}`).pipe(map((res) => {
      return new InfoBloqueioDeCreditoDTO(res)
    }));
  }

  getBoletoDoPedido(id: string): Observable<any[]> {
    return this.api.doGet(`boleto/${id}`).pipe(
      map((res) => {
        if (!res) return [];
        for (let i = 0; i < res.length; i++) {
          const b = res[i];
          this.saveBase64(b.BASE64, "application/pdf", `BOLETO_${b.PEDIDO}-${b.PARCELA}.pdf`);
        }
        return res;
      })
    );
  }
  getDanfeNfDoPedido(id: string): Observable<Blob> {
    return this.api.doGet(`nf/danfe/${id}`, { responseType: "blob" }).pipe(
      map((res) => {
        if (!res) return null;
        this.saveFile(res, `${id}-danfe.pdf`);
        return res;
      })
    );
  }

  getXmlNfDoPedido(id: string): Observable<Blob> {
    return this.api.doGet(`nf/xml/${id}`, { responseType: "blob" }).pipe(
      map((res) => {
        if (!res) return null;
        this.saveFile(res, `${id}-nf.xml`);
        return res;
      })
    );
  }
  public base64ToBlob(b64Data, contentType = "", sliceSize = 512) {
    b64Data = b64Data.replace(/\s/g, ""); //IE compatibility...
    let byteCharacters = atob(b64Data);
    let byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      let slice = byteCharacters.slice(offset, offset + sliceSize);

      let byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      let byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  public saveBase64(base64: string, contentType: string, fileName: string, sliceSize: number = 512) {
    const blob = this.base64ToBlob(base64, contentType, sliceSize)
    return this.saveFile(blob, fileName);

  }
  public saveFile(blob: Blob, fileName: string, open: boolean = true) {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      if (open)
        window.open(url);
      const link = document.createElement("a");
      link.href = url;
      link.download = "PortalREP-" + fileName;
      link.click();
    }
  }

  getCountPorFiltro(filtro: PedidosSearchFilterViewModel): Promise<number> {
    return new Promise<number>((resolver, reject) => {
      this.api.doGet(`count/lista/filtro`, { params: filtro })
        .subscribe((res) => {
          resolver(res)
        }, (error) => reject(error));
    });
  }
  getListaPorFiltro(filtro: PedidosSearchFilterViewModel): Promise<IPedidoDTO[]> {
    return new Promise<IPedidoDTO[]>((resolver, reject) => {
      this.api.doGet(`buscar/lista/filtro`, { params: filtro })
        .subscribe((res) => {
          resolver(res)
        }, (error) => reject(error));
    });
  }
}
